import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import Location from 'utils/Location';
import helperUtils from 'utils/Helpers';

export const trackSuperChatPromptClick = ({ promptText, pageContext = {} }) => {
    if (!promptText) {
        return;
    }

    const {
        pageType, productId, categoryId, categorySeoName, searchTerm
    } = pageContext;
    const eventName = 'superchat prompt click';

    const eventData = {
        linkName: eventName,
        actionInfo: eventName,
        specificEventName: eventName,
        sotType: eventName,
        sotV402: promptText
    };

    // Add page-specific SOT variables based on page type
    if (pageType === 'product' && productId) {
        eventData.sotV15 = productId;
    } else if (pageType === 'category' && categoryId) {
        eventData.sotV08 = categoryId;

        if (categorySeoName) {
            eventData.sotV163 = categorySeoName;
        }
    } else if (pageType === 'search' && searchTerm) {
        eventData.sotV104 = searchTerm;
    }

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: eventData,
        finishEventWithoutTimeout: true
    });
};

export const trackSuperChatFollowUpClick = ({ followUpText, pageContext = {}, messageId, previousMessageId }) => {
    if (!followUpText) {
        return;
    }

    const {
        pageType, productId, categoryId, categorySeoName, searchTerm
    } = pageContext;
    const eventName = 'superchat follow up click';

    const eventData = {
        linkName: eventName,
        actionInfo: eventName,
        specificEventName: eventName,
        sotType: eventName,
        sotV402: followUpText,
        sotV403: messageId || '',
        sotV404: previousMessageId || ''
    };

    // Add page-specific SOT variables based on page type
    if (pageType === 'product' && productId) {
        eventData.sotV15 = productId;
    } else if (pageType === 'category' && categoryId) {
        eventData.sotV08 = categoryId;

        if (categorySeoName) {
            eventData.sotV163 = categorySeoName;
        }
    } else if (pageType === 'search' && searchTerm) {
        eventData.sotV104 = searchTerm;
    }

    processEvent.process(anaConsts.SOT_LINK_TRACKING_EVENT, {
        data: eventData
    });
};

/**
 * Extract page context from entryPointData (used when tracking from inside SuperChat)
 * @param {Object} entryPointData - Entry point data from SuperChat
 * @returns {Object} Page context for tracking
 */
export const getSuperChatPageContextFromEntryPoint = (entryPointData = {}) => {
    // eslint-disable-next-line camelcase
    const { entrypoint, products, category_id, search_term } = entryPointData;

    if (entrypoint === 'PDP' && products && products.length > 0) {
        // eslint-disable-next-line camelcase
        const productId = products[0]?.product_id || '';

        return {
            pageType: 'product',
            productId
        };
    }

    // eslint-disable-next-line camelcase
    if (entrypoint === 'SRP' && search_term) {
        return {
            pageType: 'search',
            // eslint-disable-next-line camelcase
            searchTerm: search_term
        };
    }

    // eslint-disable-next-line camelcase
    if (entrypoint === 'PLP' && category_id) {
        // Try to get category display name for sotV163
        let categorySeoName = '';

        // Helper function to convert kebab-case to Title Case
        const kebabToTitleCase = str => helperUtils.titleCase(str.replace(/-/g, ' '));

        // First, try to get category Name from digitalData.page.category.primaryCategory
        if (digitalData?.page?.category?.primaryCategory) {
            categorySeoName = digitalData.page.category.primaryCategory;
        } else if (digitalData?.page?.pageInfo?.pageName) {
            // Extract category name from pageName (e.g., "makeup-concealer" -> "Concealer")
            const pageName = digitalData.page.pageInfo.pageName;
            const parts = pageName.split('-');
            const categoryPart = parts.length > 1 ? parts[parts.length - 1] : parts[0];

            categorySeoName = kebabToTitleCase(categoryPart);
        }

        return {
            pageType: 'category',
            // eslint-disable-next-line camelcase
            categoryId: category_id,
            categorySeoName
        };
    }

    return {
        pageType: 'unknown'
    };
};

export const getSuperChatPageContext = ({ product, nthCategory, searchKeyword }) => {
    const isSearchPage = Location.isSearchPage();
    const isProductPage = Location.isProductPage();

    if (isProductPage && product) {
        const productId = product?.productDetails?.productId || product?.productId || '';

        return {
            pageType: 'product',
            productId
        };
    }

    if (isSearchPage && searchKeyword) {
        return {
            pageType: 'search',
            searchTerm: searchKeyword
        };
    }

    if (nthCategory) {
        const categoryId = nthCategory?.categoryId || '';
        // Extract SEO name from targetUrl (e.g., "/shop/concealer" -> "concealer")
        // or from categories array if available
        let categorySeoName = '';

        if (nthCategory?.targetUrl) {
            const urlParts = nthCategory.targetUrl.split('/').filter(Boolean);
            categorySeoName = urlParts[urlParts.length - 1] || '';
        } else if (nthCategory?.categories) {
            // Find the selected category in the categories array
            const findSelectedCategory = categories => {
                for (const cat of categories) {
                    if (cat.isSelected && cat.targetUrl) {
                        const urlParts = cat.targetUrl.split('/').filter(Boolean);

                        return urlParts[urlParts.length - 1] || '';
                    }

                    if (cat.subCategories) {
                        const found = findSelectedCategory(cat.subCategories);

                        if (found) {
                            return found;
                        }
                    }
                }

                return '';
            };
            categorySeoName = findSelectedCategory(nthCategory.categories);
        }

        return {
            pageType: 'category',
            categoryId,
            categorySeoName
        };
    }

    return {
        pageType: 'unknown'
    };
};
